import React, { useState, useEffect } from 'react'
import { Button, Row, Col, Form, Card, Badge } from 'react-bootstrap'
import ModalContainer from './ModalContainer'
import Message from '../../shared_components/Message'

const isValidOptionalPackageId = (pkg) =>
  pkg._id &&
  typeof pkg._id === 'string' &&
  pkg._id.length === 24 &&
  /^[0-9a-fA-F]{24}$/.test(pkg._id)

const togglePackageInList = (currentList, packageId) => {
  const tempList = JSON.parse(JSON.stringify(currentList))
  const idx = tempList.indexOf(packageId)
  if (idx >= 0) {
    tempList.splice(idx, 1)
  } else {
    tempList.push(packageId)
  }
  return tempList
}

const PackageMultiSelect = ({ packages, selectedIds, onToggle, emptyMessage, controlId }) => {
  if (packages.length === 0) {
    return (
      <div
        style={{
          padding: '10px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
        }}
      >
        <p style={{ margin: 0, color: '#6c757d', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <Form.Control
      as='select'
      value={selectedIds}
      onChange={() => {}}
      onClick={(e) => {
        if (!e.target.value || e.target.value.trim() === '') return
        onToggle(e.target.value)
      }}
      multiple
      style={{ minHeight: '100px' }}
      id={controlId}
    >
      {packages.map((pkg) => (
        <option key={controlId + pkg._id} value={pkg._id}>
          {pkg.title}
        </option>
      ))}
    </Form.Control>
  )
}

export const JitaiOptionalTxPackageAssignmentSetting = ({
  group,
  index,
  jitai_decision_rules,
  study,
  stage,
  handleDelete,
}) => {
  const [show, setShow] = useState(false)

  const [assignOptionalTxPackagesRule, setAssignOptionalTxPackagesRule] = useState(null)
  const [addedOptionalTxPackageList, setAddedOptionalTxPackageList] = useState([])
  const [elseAddedOptionalTxPackageList, setElseAddedOptionalTxPackageList] = useState([])
  const [optionalModulesParticipantGroup, setOptionalModulesParticipantGroup] = useState(null)

  const [saveError, setSaveError] = useState('')

  const getNextStage = () => {
    if (!study || !stage) return null
    return study.stage_list.find((s) => s.stage_num === stage.stage_num + 1) || null
  }

  const getParticipantGroupsNextStage = () => {
    const ns = getNextStage()
    return ns?.participant_group_list || []
  }

  const getOptionalPackagesForScopeGroupId = (scopeGroupId) => {
    if (!scopeGroupId) return []
    const ns = getNextStage()
    if (!ns?.participant_group_list) return []
    const g = ns.participant_group_list.find((x) => String(x._id) === String(scopeGroupId))
    if (!g?.tx_package_list) return []
    return g.tx_package_list.filter((pkg) => pkg.is_optional === true && isValidOptionalPackageId(pkg))
  }

  useEffect(() => {
    const setting = group.next_stage_optional_tx_package_settings[index]
    setAssignOptionalTxPackagesRule(setting.assign_optional_tx_packages_rule || null)
    setAddedOptionalTxPackageList(setting.added_optional_tx_package_list || [])
    setElseAddedOptionalTxPackageList(setting.else_added_optional_tx_package_list || [])
    setOptionalModulesParticipantGroup(setting.optional_modules_participant_group || null)
  }, [group, index])

  const handleOpen = () => {
    setShow(true)
  }

  const checkSettings = () => {
    if (!assignOptionalTxPackagesRule) {
      return 'Decision Rule must be selected'
    }
    if (!optionalModulesParticipantGroup) {
      return 'Participant group for optional modules must be selected'
    }
    if (!addedOptionalTxPackageList.length) {
      return 'At least one chapter must be selected for "Then assign"'
    }
    return ''
  }

  const handleClose = () => {
    setSaveError('')
    let error = checkSettings()

    if (error.length === 0) {
      group.next_stage_optional_tx_package_settings[index].assign_optional_tx_packages_rule =
        assignOptionalTxPackagesRule
      group.next_stage_optional_tx_package_settings[index].added_optional_tx_package_list =
        addedOptionalTxPackageList
      group.next_stage_optional_tx_package_settings[index].else_added_optional_tx_package_list =
        elseAddedOptionalTxPackageList
      group.next_stage_optional_tx_package_settings[index].optional_modules_participant_group =
        optionalModulesParticipantGroup
      setShow(false)
    } else {
      setSaveError(error)
    }
  }

  const getDecisionRuleCode = () => {
    if (!assignOptionalTxPackagesRule) return 'Not Set'
    const rule = jitai_decision_rules?.find((r) => r._id === assignOptionalTxPackagesRule)
    return rule ? rule.code : 'Unknown Rule'
  }

  const scopeGroups = getParticipantGroupsNextStage()
  const scopeLabel =
    scopeGroups.find((g) => String(g._id) === String(optionalModulesParticipantGroup))?.name || ''

  const filteredOptionalPackages = getOptionalPackagesForScopeGroupId(optionalModulesParticipantGroup)

  const getPackageNames = (packageIdList) => {
    return packageIdList.map((pkgId) => {
      const pkg = filteredOptionalPackages.find((p) => String(p._id) === String(pkgId))
      return pkg ? pkg.title : `Unknown (${pkgId})`
    })
  }

  const renderPackageBadges = (names) => {
    if (names.length === 0) {
      return <span style={{ fontStyle: 'italic', color: '#6c757d' }}>(none selected)</span>
    }
    return names.map((name, i) => (
      <Badge key={i} variant='info' className='mr-1'>
        {name}
      </Badge>
    ))
  }

  const thenNames = getPackageNames(addedOptionalTxPackageList)
  const elseNames = getPackageNames(elseAddedOptionalTxPackageList)
  const hasElse = elseAddedOptionalTxPackageList.length > 0

  const emptyPackageMessage = optionalModulesParticipantGroup
    ? 'No optional modules for this group in the next stage. Mark chapters as Optional Module on that group and save the study first.'
    : 'Select a participant group above to list optional chapters.'

  return (
    <>
      <Card className='p-2 my-2 rounded'>
        <Row style={{ marginLeft: '-5px', marginBottom: '10px' }}>
          <Col md={12} style={{ whiteSpace: 'normal' }}>
            <div style={{ lineHeight: 1.45 }}>
              If{' '}
              <span style={{ color: '#0056b3' }}>{getDecisionRuleCode()}</span>
              {' '}when automatically assigned to{' '}
              {scopeLabel ? (
                <Badge variant='secondary'>{scopeLabel}</Badge>
              ) : (
                <span style={{ fontStyle: 'italic', color: '#6c757d' }}>(participant group not set)</span>
              )}
              , then assign{' '}
              {renderPackageBadges(thenNames)}
              {hasElse && (
                <>
                  {' '}else{' '}
                  {renderPackageBadges(elseNames)}
                </>
              )}
            </div>
          </Col>
        </Row>
        <Row style={{ marginLeft: '-5px' }}>
          <Col md={6}></Col>
          <Col md={3}>
            <Button
              style={{ width: '100%' }}
              variant='primary'
              className='btn-sm'
              onClick={handleOpen}
            >
              Edit
            </Button>
          </Col>
          <Col md={3}>
            <Button
              style={{ width: '100%', marginLeft: '-12px' }}
              variant='danger'
              className='btn-sm'
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Col>
        </Row>
      </Card>

      <ModalContainer title='Edit Optional Treatment Chapter Setting' show={show} handleClose={handleClose}>
        {saveError && <Message variant='danger'>{saveError}</Message>}

        <Row>
          <Col md={12}>
            <Form.Group controlId={'optional_tx_packages_rule' + group._id + index}>
              <Form.Label>
                Optional Treatment Chapter Assignment Rule <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              {jitai_decision_rules &&
              jitai_decision_rules.filter((rule) => rule.type === 'optionalTxPackageAssignment').length === 0 ? (
                <div
                  style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                >
                  <p style={{ margin: 0, color: '#6c757d', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    No Optional Treatment Chapter Assignment decision rules available. Please create one first.
                  </p>
                </div>
              ) : (
                <Form.Control
                  as='select'
                  value={assignOptionalTxPackagesRule || ''}
                  onChange={(e) => {
                    setAssignOptionalTxPackagesRule(e.target.value || null)
                  }}
                >
                  <option value=''>—Select Decision Rule—</option>
                  {jitai_decision_rules &&
                    jitai_decision_rules
                      .filter((rule) => rule.type === 'optionalTxPackageAssignment')
                      .map((rule) => (
                        <option key={rule._id} value={rule._id}>
                          {rule.code}
                        </option>
                      ))}
                </Form.Control>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row style={{ marginTop: '15px' }}>
          <Col md={12}>
            <Form.Group controlId={'optional_modules_scope_group' + group._id + index}>
              <Form.Label>
                Participant group (next stage) whose optional modules apply{' '}
                <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                as='select'
                value={optionalModulesParticipantGroup || ''}
                onChange={(e) => {
                  const v = e.target.value || null
                  setOptionalModulesParticipantGroup(v)
                  setAddedOptionalTxPackageList([])
                  setElseAddedOptionalTxPackageList([])
                }}
              >
                <option value=''>—Select participant group—</option>
                {scopeGroups.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.name}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className='text-muted'>
                Only optional treatment chapters marked on this group in the next stage can be selected below.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row style={{ marginTop: '15px' }}>
          <Col md={12}>
            <Form.Group controlId={'added_optional_tx_packages' + group._id + index}>
              <Form.Label>
                Then assign (when rule is true) <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <PackageMultiSelect
                packages={filteredOptionalPackages}
                selectedIds={addedOptionalTxPackageList}
                onToggle={(packageId) =>
                  setAddedOptionalTxPackageList(togglePackageInList(addedOptionalTxPackageList, packageId))
                }
                emptyMessage={emptyPackageMessage}
                controlId={'then_optional_tx_' + group._id + index}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row style={{ marginTop: '15px' }}>
          <Col md={12}>
            <Form.Group controlId={'else_added_optional_tx_packages' + group._id + index}>
              <Form.Label>Else assign (when rule is false, optional)</Form.Label>
              <PackageMultiSelect
                packages={filteredOptionalPackages}
                selectedIds={elseAddedOptionalTxPackageList}
                onToggle={(packageId) =>
                  setElseAddedOptionalTxPackageList(
                    togglePackageInList(elseAddedOptionalTxPackageList, packageId)
                  )
                }
                emptyMessage={emptyPackageMessage}
                controlId={'else_optional_tx_' + group._id + index}
              />
            </Form.Group>
          </Col>
        </Row>
      </ModalContainer>
    </>
  )
}

export default JitaiOptionalTxPackageAssignmentSetting
